import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { evaluationFrameworksApi } from '../services/api';
import { FrameworkMetricCreate, FrameworkMetricUpdate, FrameworkMetric } from '../types';

interface MetricModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  frameworkId: number;
  editingMetric?: FrameworkMetric | null;
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const MetricModal: React.FC<MetricModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  frameworkId,
  editingMetric,
  addNotification
}) => {
  const [formData, setFormData] = useState<FrameworkMetricCreate>({
    framework_id: frameworkId,
    metric_type: 'faithfulness',
    name: '',
    description: '',
    status: 'active',
    is_required: false,
    default_weight: 1.0,
    priority: 1,
    score_range_min: 0.0,
    score_range_max: 1.0,
    score_description: '',
    implementation_class: '',
    parameters: '',
    dependencies: '',
    documentation_url: '',
    example_usage: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [metricTypes, setMetricTypes] = useState<string[]>([]);
  const [metricStatuses, setMetricStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadMetadata();
      if (editingMetric) {
        setFormData({
          framework_id: frameworkId,
          metric_type: editingMetric.metric_type,
          name: editingMetric.name,
          description: editingMetric.description || '',
          status: editingMetric.status,
          is_required: editingMetric.is_required,
          default_weight: editingMetric.default_weight,
          priority: editingMetric.priority,
          score_range_min: editingMetric.score_range_min,
          score_range_max: editingMetric.score_range_max,
          score_description: editingMetric.score_description || '',
          implementation_class: editingMetric.implementation_class || '',
          parameters: editingMetric.parameters || '',
          dependencies: editingMetric.dependencies || '',
          documentation_url: editingMetric.documentation_url || '',
          example_usage: editingMetric.example_usage || ''
        });
      } else {
        // Reset form for new metric
        setFormData({
          framework_id: frameworkId,
          metric_type: 'faithfulness',
          name: '',
          description: '',
          status: 'active',
          is_required: false,
          default_weight: 1.0,
          priority: 1,
          score_range_min: 0.0,
          score_range_max: 1.0,
          score_description: '',
          implementation_class: '',
          parameters: '',
          dependencies: '',
          documentation_url: '',
          example_usage: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, editingMetric, frameworkId]);

  const loadMetadata = async () => {
    try {
      const [types, statuses] = await Promise.all([
        evaluationFrameworksApi.getMetricTypes(),
        evaluationFrameworksApi.getMetricStatuses()
      ]);
      setMetricTypes(types);
      setMetricStatuses(statuses);
    } catch (error) {
      console.error('Error loading metadata:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Metric name is required';
    }

    if (!formData.metric_type) {
      newErrors.metric_type = 'Metric type is required';
    }

    if (formData.score_range_min !== undefined && formData.score_range_max !== undefined && formData.score_range_min >= formData.score_range_max) {
      newErrors.score_range_max = 'Max score must be greater than min score';
    }

    if (formData.default_weight !== undefined && formData.default_weight < 0) {
      newErrors.default_weight = 'Weight must be non-negative';
    }

    if (formData.priority !== undefined && formData.priority < 1) {
      newErrors.priority = 'Priority must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (editingMetric) {
        await evaluationFrameworksApi.updateFrameworkMetric(editingMetric.id, formData);
        addNotification('Metric updated successfully', 'success');
      } else {
        await evaluationFrameworksApi.createFrameworkMetric(frameworkId, formData);
        addNotification('Metric created successfully', 'success');
      }
      
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving metric:', error);
      addNotification(error.response?.data?.detail || 'Failed to save metric', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FrameworkMetricCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-slate-800 border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-100">
            {editingMetric ? 'Edit Metric' : 'Add New Metric'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-slate-700/50 rounded-lg p-6 border border-slate-600">
            <h3 className="text-lg font-medium text-slate-100 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Metric Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.name ? 'border-red-500' : 'border-slate-600'
                  }`}
                  placeholder="e.g., Faithfulness, Answer Relevancy"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Metric Type *
                </label>
                <select
                  value={formData.metric_type}
                  onChange={(e) => handleInputChange('metric_type', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.metric_type ? 'border-red-500' : 'border-slate-600'
                  }`}
                >
                  {metricTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
                {errors.metric_type && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.metric_type}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {metricStatuses.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={formData.is_required}
                  onChange={(e) => handleInputChange('is_required', e.target.checked)}
                  className="h-4 w-4 text-indigo-500 focus:ring-indigo-500 border-slate-600 rounded bg-slate-800"
                />
                <label htmlFor="is_required" className="ml-2 text-sm text-slate-200">
                  Required Metric
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Describe what this metric measures..."
                />
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="bg-slate-700/50 rounded-lg p-6 border border-slate-600">
            <h3 className="text-lg font-medium text-slate-100 mb-4">Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Default Weight
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.default_weight}
                  onChange={(e) => handleInputChange('default_weight', parseFloat(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.default_weight ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
                {errors.default_weight && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.default_weight}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Priority
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.priority ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
                {errors.priority && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.priority}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Score Range Min
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.score_range_min}
                  onChange={(e) => handleInputChange('score_range_min', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Score Range Max
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.score_range_max}
                  onChange={(e) => handleInputChange('score_range_max', parseFloat(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.score_range_max ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
                {errors.score_range_max && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.score_range_max}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Score Description
                </label>
                <input
                  type="text"
                  value={formData.score_description}
                  onChange={(e) => handleInputChange('score_description', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., 0-1 scale, where 1.0 represents perfect performance"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : editingMetric ? 'Update Metric' : 'Create Metric'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MetricModal;

